// src/stores/useCaseBuilderStore.js
//
// Single source of truth for the in-progress "Create New Case" DRAFT PADS (the
// Explore map's case-builder mode). Holds the selected points' buffered pad
// squares ONLY — name / participants / autodetect are NOT collected here; the
// builder ends with "Continue to case details," which hands this draft to the
// unified NewCaseForm (QcPicker.jsx) where the rest of the case metadata is
// collected and the case is created (POST /cases/from-selection).
//
// App-level (not component) state so it survives the builder→form→dashboard
// navigation. Mirrored to localStorage (persist middleware) so a hard refresh /
// tab close / crash can offer to resume an in-progress draft; cleared on submit
// (from the form) or Cancel. A beforeunload warning (installed by the builder
// UI) guards against accidental loss while a draft has pads.

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { PERSIST_KEYS } from "../auth/persistKeys";

const DEFAULT_BUFFER_M = 200;

function newId() {
  // Browser-native; falls back to a time-ish token if unavailable.
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `pad-${Math.floor(performance.now() * 1000)}`;
}

// Fields worth persisting for crash-recovery — the draft itself, not the
// transient UI selection/active flags.
const PERSISTED = (s) => ({
  sourceLayerId: s.sourceLayerId,
  bufferMeters: s.bufferMeters,
  pads: s.pads,
  returnTo: s.returnTo,
});

export const useCaseBuilderStore = create(
  persist(
    (set, get) => ({
      // ── transient ──
      active: false,
      selectedPadId: null,   // pad currently loaded into MlDraw for vertex edit
      highlightPadId: null,  // row↔map hover highlight
      focusedPadId: null,    // pad the list highlights + the map last zoomed to
      zoomNonce: 0,          // bump to re-request a zoom-to even for the same pad

      // ── persisted draft ──
      returnTo: null,
      sourceLayerId: null,
      bufferMeters: DEFAULT_BUFFER_M,
      pads: [],              // [{ id, pad_name, state_code, region, geometry }]

      // ── lifecycle ──
      // Enter builder. Keeps any persisted draft (resume); the UI surfaces a
      // resume/discard notice when pads already exist.
      start(returnTo = null) {
        set({ active: true, returnTo: returnTo ?? get().returnTo ?? null });
      },
      // Leave builder UI but keep the persisted draft (e.g. nav away).
      exit() {
        set({ active: false, selectedPadId: null, highlightPadId: null, focusedPadId: null });
      },
      // Discard the draft contents but stay in the builder.
      discardDraft() {
        set({
          pads: [],
          selectedPadId: null,
          highlightPadId: null,
          focusedPadId: null,
        });
      },
      // Full reset (Cancel / case submitted) — clears everything incl. active.
      reset() {
        set({
          active: false,
          selectedPadId: null,
          highlightPadId: null,
          focusedPadId: null,
          returnTo: null,
          sourceLayerId: null,
          bufferMeters: DEFAULT_BUFFER_M,
          pads: [],
        });
      },

      // ── source + buffer ──
      setSourceLayer(id) { set({ sourceLayerId: id }); },
      setBufferMeters(m) {
        const v = Number(m);
        set({ bufferMeters: Number.isFinite(v) && v > 0 ? v : DEFAULT_BUFFER_M });
      },

      // ── pads ──
      // points: [{ lon, lat, name }]; squares are built by the caller (it owns
      // the buffer math + current bufferMeters) and passed in as full pad rows.
      setPads(pads) { set({ pads, selectedPadId: null, focusedPadId: null }); },
      updatePad(id, patch) {
        set({ pads: get().pads.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
      },
      replacePadGeometry(id, geometry) {
        set({ pads: get().pads.map((p) => (p.id === id ? { ...p, geometry } : p)) });
      },
      removePad(id) {
        const pads = get().pads.filter((p) => p.id !== id);
        set({
          pads,
          selectedPadId: get().selectedPadId === id ? null : get().selectedPadId,
          focusedPadId: get().focusedPadId === id ? null : get().focusedPadId,
        });
      },
      setSelectedPad(id) { set({ selectedPadId: id }); },
      setHighlightPad(id) { set({ highlightPadId: id }); },
      // Select a pad (list highlight) and request a map zoom-to. The nonce bump
      // re-fires the zoom even when the same pad is already focused (e.g. the
      // user panned away then clicked zoom again). The actual fitBounds lives in
      // CaseBuilderMap, which owns the map handle + readiness.
      focusPad(id) { set({ focusedPadId: id, zoomNonce: get().zoomNonce + 1 }); },

      // Merge classify-pads results (authoritative state + in-region verdict)
      // back onto pads by id.
      applyClassification(byId) {
        set({
          pads: get().pads.map((p) =>
            byId[p.id]
              ? { ...p, state_code: byId[p.id].state ?? p.state_code, region: byId[p.id].verdict }
              : p
          ),
        });
      },

      // Helper so the UI doesn't re-implement id generation.
      makePadId: newId,
    }),
    {
      name: PERSIST_KEYS.caseBuilderDraft,
      partialize: PERSISTED,
    }
  )
);
